import React, { useCallback, useMemo } from 'react';
import { Box, Column, InputMax } from '../../core';
import {
  DEFAULT_DEPOSIT_WITHDRAW_SLIPPAGE,
  formatAmountBN,
  SCYVault,
  toBN,
  useCall2,
  useERC20,
  useInputState,
  useSend,
} from '@sc1/common';
import { StatusButton } from '../../StatusButton';

interface WithdrawProps {
  vault: SCYVault;
  account: string;
}

export const ScyWithdraw = ({ vault, account }: WithdrawProps): JSX.Element => {
  const { amountStr, amountBN, amountIsMax, setAmountWithMax, setMax } =
    useInputState();

  const { sendTx, txStatus } = useSend();

  const { res: balanceSharesBN = toBN(0) } = useCall2(
    vault?.balanceOf,
    account
  );

  const { res: balanceUnderlyingBN = toBN(0) } = useCall2(
    vault?.sharesToUnderlying,
    balanceSharesBN
  );

  const { res: decUnderlying } = useCall2(vault?.underlyingDecimals);

  const { res: underlyingAddress } = useCall2(vault?.underlying);
  const underlying = useERC20(underlyingAddress);
  const { res: underlyingSymbol } = useCall2(underlying?.symbol);

  const minTokenOut = useMemo(
    () => amountBN?.mul(DEFAULT_DEPOSIT_WITHDRAW_SLIPPAGE)?.div(10000),
    [amountBN, DEFAULT_DEPOSIT_WITHDRAW_SLIPPAGE]
  );

  const { res: sharesBN = toBN(0) } = useCall2(
    vault?.underlyingToShares,
    amountBN
  );

  const balanceFmt = useMemo(
    () => formatAmountBN(balanceUnderlyingBN, decUnderlying, false, 2, false),
    [balanceUnderlyingBN, decUnderlying]
  );

  const withdraw = useCallback(
    () =>
      vault &&
      sharesBN &&
      minTokenOut &&
      underlyingAddress &&
      sendTx(vault, 'redeem', [
        account,
        amountIsMax ? balanceSharesBN : sharesBN,
        underlyingAddress,
        minTokenOut,
      ]),
    [
      vault,
      sendTx,
      account,
      sharesBN,
      balanceSharesBN,
      underlyingAddress,
      minTokenOut,
    ]
  );
  return (
    <Column sx={{ flex: 1 }}>
      <Box sx={{ fontSize: 1 }}>
        Balance: {balanceFmt} {underlyingSymbol}
      </Box>
      <InputMax
        my={2}
        value={amountStr}
        placeholder="0"
        type="number"
        min="0"
        onMaxClick={() => {
          setMax(balanceUnderlyingBN, decUnderlying);
        }}
        onChange={(e) => {
          setAmountWithMax(e.target.value, decUnderlying, balanceUnderlyingBN);
        }}
      />
      <StatusButton onClick={withdraw} status={txStatus}>
        Withdraw
      </StatusButton>
    </Column>
  );
};
